import { ConfiguratorState } from '@/app/store/configurator/types/configurator.types';
import { FREESTANDING_SECTIONS_WIDTH } from '@/shared/constants/configurator';
import {
	calculateConfiguratorDepth,
	calculateConfiguratorHeight,
	calculateConfiguratorWidth,
	calculateRackDepth,
} from '@/shared/lib/configurator';
import { MODEL_DISPLAY_SCALE, MODEL_SIDES_DISTANCE } from '../constants';
import { RenderComponentStrategy } from '../render-components/render-component.strategy';
import { SizeArrow } from '../render-components/size-arrow';
import { Nodes } from '../types/model.types';
import { SectionBuilder } from './section.builder';

export class ModelBuilder {
	private components: RenderComponentStrategy[] = [];

	constructor(
		private readonly state: ConfiguratorState,
		private readonly nodes: Nodes,
		private readonly canvasWidth: number,
	) {}

	render() {
		this.components = [];
		this.build();

		return this.components.map((component) => {
			const { position } = component.options;

			component.setPosition([position[0] - this.canvasWidth / 1.5, position[1], position[2]]);

			return component.render(this.nodes);
		});
	}

	private build() {
		this.addSectionsWall({ side: 'front' });

		if (this.state.doubleSided) {
			this.addSectionsWall({ side: 'back' });
		}

		this.addSizes();
	}

	private addSectionsWall({ side }: { side: 'front' | 'back' }) {
		let x = 0;
		const z =
			side === 'front'
				? 0
				: (calculateRackDepth(this.state.sections) + MODEL_SIDES_DISTANCE) *
					-MODEL_DISPLAY_SCALE;

		for (let index = 0; index < this.state.sections.length; index++) {
			const section = this.state.sections[index];

			const sectionComponents = new SectionBuilder(this.state).build({
				section,
				index,
				side,
				position: {
					x,
					z,
					y: 0,
				},
			});

			this.components.push(...sectionComponents);

			x += section.width * MODEL_DISPLAY_SCALE;

			if (!this.state.wallMounted) {
				x += (FREESTANDING_SECTIONS_WIDTH + 80) * MODEL_DISPLAY_SCALE;
			}
		}
	}

	private addSizes() {
		const { sections, wallMounted, doubleSided } = this.state;

		const width = calculateConfiguratorWidth(sections, wallMounted);
		const height = calculateConfiguratorHeight(sections);
		const depth = calculateConfiguratorDepth(sections, doubleSided);

		this.components.push(
			new SizeArrow({
				color: 'black',
				rotation: [Math.PI / 2, 0, Math.PI / 2],
				position: [(width * MODEL_DISPLAY_SCALE) / 2 - 0.05, -1.25, 0.15],
				scale: [1, width * MODEL_DISPLAY_SCALE, 1],
				options: { label: `${width} mm` },
			}),
			new SizeArrow({
				color: 'black',
				rotation: [0, 0, 0],
				position: [-0.15, (height * MODEL_DISPLAY_SCALE) / 2 - 1.25, 0.15],
				scale: [1, height * MODEL_DISPLAY_SCALE, 1],
				options: { label: `${height} mm` },
			}),
			new SizeArrow({
				color: 'black',
				rotation: [Math.PI / 2, 0, 0],
				position: [-0.15, -1.25, -(depth * MODEL_DISPLAY_SCALE) / 2],
				scale: [1, depth * MODEL_DISPLAY_SCALE, 1],
				options: { label: `${depth} mm` },
			}),
		);
	}
}
